import { useQuery } from "@tanstack/react-query";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { Tag } from "@shared/types/tags";

type TagContext = "patients" | "attendees";

type UseTagsResult = {
  tags: Tag[];
  isLoading: boolean;
  error: Error | null;
};

/**
 * Hook to fetch tags for patients or attendees.
 * Tags are cached per healthcare company and context.
 */
const useTags = (
  hccId: string,
  context: TagContext = "patients"
): UseTagsResult => {
  const { GET } = useApiRequest();

  const endpoint =
    context === "patients"
      ? `/api/healthcare-companies/${hccId}/patients/tags`
      : `/api/healthcare-companies/${hccId}/e/attendees/tags`;

  const queryKey =
    context === "patients" ? ["patient-tags", hccId] : ["attendee-tags", hccId];

  const { data: tags = [], isLoading, error } = useQuery<Tag[]>({
    queryKey,
    queryFn: async () => {
      const fetchedTags = await GET<Tag[]>(endpoint);
      return fetchedTags.map((tag) => ({
        ...tag,
        color: tag.color || "hsl(0, 0%, 80%)", // Assign default color if missing
      }));
    },
    enabled: Boolean(hccId),
  });

  return {
    tags,
    isLoading,
    error: error ?? null,
  } satisfies UseTagsResult;
};

export default useTags;
